// src/oura/components/docs/DocRow.tsx
//
// One row per doc on the staff documents board, and one per doc the plan
// expects with none shared. A row is one line: the file's icon, its title and
// code chips, then one fact on the right (how long it has waited, its
// reply-by, or its late note in rose). A tag shows only what the group head
// does not already say: a red sharing flag, an amber naming flag, "Client
// late" on a doc that needs us, and the status in By code. A doc that needs
// us adds the client's last words on a second line. Every other fact is in
// the drawer, one click away.
//
// Every doc row opens the drawer: the row is a plain box whose title button
// stretches over it, with "Pick a code", Delete and the Open icon above that.
//
// The row reads its own width (a container query), so it stacks in a narrow
// card as it does on a phone. Under 40rem the chips, the fact and the tags
// sit on a line under the title; from 40rem the chips join the title and the
// fact goes right; from 56rem the tags go right too (64rem for 3 tags).

import { useState, type ComponentProps, type ReactNode } from "react";
import { CheckIcon, ChevronRightIcon, FileQuestionIcon, HashIcon, Loader2Icon, Trash2Icon } from "lucide-react";
import { markDocumentDone, type StaffDocument } from "@service/aeo/clientDocs";
import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from "@component/tooltip";
import { useWritesPaused } from "@context/maintenance";
import { cn } from "../../lib/cn";
import { DOC_FLAG, DOC_STATUS, Dot, FOCUS_RING, FamilyChip, INSET_HAIRLINE, IconSquare, LATE_TEXT, PillButton, ROW_HOVER, RowOpenLink, TAP_TARGET, TYPE, Tag, type StatusStyle } from "@oura/ui";
import {
    codeDueText,
    codeFamily,
    codeLabel,
    codeRowName,
    docCodeChips,
    docDate,
    docLateNote,
    docStatusKey,
    fileStyle,
    replyByText,
    staffDocTitle,
    trimText,
    waitingDaysText,
} from "@oura/docs";
import { rowRunTag } from "../../docs/skillRun";
import { prefillFromWord, type CreatePrefill } from "./create/createModel";
import { DeleteDocDialog } from "./DeleteDocDialog";
import { MarkDoneDialog } from "./MarkDoneDialog";
import { BOARD_GROUP, UNKNOWN_FLAG_LABEL, flagTagLabel, groupOfDoc, rowAmberFlag, type BoardGroupKey, type DocToSend } from "./boardModel";


/** The row's box: a size container its parts read, with the hairline starting where the text starts. */
const SHELL = cn("[container-type:inline-size]", INSET_HAIRLINE("square"), ROW_HOVER);
/** Wraps only when narrow, so "Pick a code" can drop under the text there. */
const ROW = "flex min-h-12 w-full flex-wrap items-start gap-x-3 gap-y-1 py-2 [@container(min-width:40rem)]:flex-nowrap";
/** Shown only under 40rem: the copies that sit on the line under the title. Literal, so the JIT sees it. */
const NARROW_ONLY = "[@container(min-width:40rem)]:hidden";
/**
 * From how wide the tags join line 1: 56rem, or 64rem for 3 of them, so they
 * never squeeze the title to a word. Literal, so the JIT sees each class.
 */
const TAGS_WIDE = {
    some: { show: "[@container(min-width:56rem)]:flex", hide: "[@container(min-width:56rem)]:hidden" },
    many: { show: "[@container(min-width:64rem)]:flex", hide: "[@container(min-width:64rem)]:hidden" },
} as const;
/** The title: 2 lines when narrow, 1 cut line from 40rem. */
const TITLE_CUT = "line-clamp-2 break-words [@container(min-width:40rem)]:line-clamp-none [@container(min-width:40rem)]:truncate";

/** The 1 fact on the right of a row. */
interface RowFact {
    text: string;
    late: boolean;
}

/**
 * How long a doc that needs us has waited on us ("Waiting 6 working days"),
 * or a waiting doc's reply-by; a waiting doc's late note instead once it is
 * late. A doc that needs us keeps its wait: its lateness is the client's (on
 * questions still with them) and shows as a "Client late" tag, never in the
 * fact where it would read as ours. Other groups have no fact: nothing in
 * them waits on anyone. The drawer says both in full.
 */
function docFact(doc: StaffDocument, group: BoardGroupKey): RowFact | null {
    // Marked done by staff (#4541): when, and by whom.
    if (group === "done") return doc.done ? { text: `Done ${docDate(doc.done.at) ?? ""} by ${doc.done.by}`.replace("  ", " "), late: false } : null;
    if (group === "needs_us") {
        const text = waitingDaysText(doc.waitingWorkingDays);
        return text ? { text, late: false } : null;
    }
    if (group !== "waiting_on_client") return null;
    const late = docLateNote(doc, "client");
    if (late) return { text: late, late: true };
    const text = replyByText(doc.replyBy) ?? waitingDaysText(doc.waitingWorkingDays);
    return text ? { text, late: false } : null;
}

/** The fact, grey or rose, kept whole. */
function Fact({ fact, className }: { fact: RowFact; className?: string }) {
    return <span className={cn(TYPE.tc, "whitespace-nowrap tabular-nums", fact.late ? LATE_TEXT : "text-gray-500", className)}>{fact.text}</span>;
}

/**
 * A row's tag, held to one line: it is cut with an ellipsis at 16rem, so no
 * label can run over the title and chips beside it. Labels are meant to be
 * short facts; this is the guard for the one that is not.
 */
function RowTag({ tone, children }: { tone: ComponentProps<typeof Tag>["tone"]; children: ReactNode }) {
    return (
        <Tag tone={tone} className="max-w-[16rem]">
            <span className="truncate">{children}</span>
        </Tag>
    );
}

/**
 * The file's full name on hover and on keyboard focus, over a title that is
 * cut to 1 line (2 when narrow). A tap opens the doc and shows no tip.
 */
function FullName({ name, children }: { name: string; children: ReactNode }) {
    return (
        <TooltipProvider>
            <TooltipRoot delayDuration={300}>
                <TooltipTrigger asChild>{children}</TooltipTrigger>
                <TooltipContent side="top" align="start" className="max-w-[min(28rem,calc(100vw-2rem))] break-words">
                    {name}
                </TooltipContent>
            </TooltipRoot>
        </TooltipProvider>
    );
}

export function DocRow({
    doc,
    onOpen,
    onPick,
    onCreate,
    showStatus = false,
    due = null,
    meta = null,
    viewLabel,
    canMarkDone = false,
    canDelete = false,
}: {
    doc: StaffDocument;
    /** The board offers Delete on its rows (#4631) where Docs write is on: the file goes to the Drive trash. */
    canDelete?: boolean;
    /** The board offers Mark as done / Undo on its rows (#4541), where the backend serves `done`. */
    canMarkDone?: boolean;
    onOpen: (id: string) => void;
    onPick: (id: string) => void;
    /** Opens Create on a Word file with the file picked: the row's "Convert to a Google Doc" (#4307). Absent, the row offers none. */
    onCreate?: (prefill: CreatePrefill) => void;
    /** By code, where no group head says the doc's status: a status tag on the row. */
    showStatus?: boolean;
    /**
     * When a doc that exists but is not shared was due, from the plan lines it
     * covers. The only fact a Not shared row has: the plan's own row for it is
     * not listed beside it any more.
     */
    due?: RowFact | null;
    /** A quiet line under the title: the Documents page tells same-named docs apart by version and date. */
    meta?: ReactNode;
    /** "View": the Documents page ends the row with "View ›", as ArrowLink draws it, in place of the bare chevron. The row stays one button. */
    viewLabel?: string;
}) {
    const group = groupOfDoc(doc);
    const style = BOARD_GROUP[group].style;
    const plain = group === "no_code" || group === "internal";
    const muted = Boolean(style.muted);
    const file = fileStyle(doc.fileType);
    const title = plain ? doc.name : staffDocTitle(doc);
    const chips = docCodeChips(doc);
    const fact = docFact(doc, group) ?? (group === "not_shared" ? due : null);
    const [doneBusy, setDoneBusy] = useState(false);
    const [doneFailed, setDoneFailed] = useState<string | null>(null);
    // Mark as done asks the day the work was done in a dialog (MarkDoneDialog), which saves it.
    const [doneOpen, setDoneOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const writesPaused = useWritesPaused();
    const openDone = () => {
        setDoneFailed(null);
        setDoneOpen(true);
    };
    // Undo is one click: no date to ask.
    const undoDone = async () => {
        setDoneBusy(true);
        setDoneFailed(null);
        const r = await markDocumentDone(doc.id, false);
        setDoneBusy(false);
        if (!r.ok) setDoneFailed("That did not go through. Try again.");
    };

    const statusKey = docStatusKey(doc);
    // A state this build does not know shows no tag, never a crash.
    const status: StatusStyle | null = !showStatus
        ? null
        : group === "internal"
            ? { label: "Internal", tone: "gray" }
            : statusKey
                ? DOC_STATUS[statusKey]?.staff ?? null
                : null;
    // One red tag: the flag's fact, or how many when there are more; the drawer names each.
    const red = doc.flags.filter((f) => f.tone === "red");
    const redTag = red.length === 1
        ? flagTagLabel(doc, red[0].code, DOC_FLAG[red[0].code]?.label ?? UNKNOWN_FLAG_LABEL)
        : red.length > 1
            ? `${red.length} red flags`
            : null;
    // A converted Word file says so in its own line, with the Google Doc's name, not as a bare tag.
    const converted = doc.fileType === "word" ? doc.convertedTo ?? null : null;
    const rawAmber = rowAmberFlag(doc, group);
    const amber = converted && rawAmber?.code === "word_file" ? undefined : rawAmber;
    // A doc that needs us can be late on the client's side: questions still
    // with them are past reply-by. The drawer says by how much.
    const lateOnClient = group === "needs_us" && doc.late === true;
    // One status and at most ONE warning per row (Jack, 22 Oct: rows carried
    // four coloured tags). The worst wins: a red flag, then an amber, then the
    // client being late. The drawer still lists every one.
    const warning = redTag ? "red" : amber ? "amber" : lateOnClient ? "late" : null;
    const clientLate = warning === "late";
    // A doc a skill is still writing says so, or it is opened to a placeholder (#4391).
    const run = rowRunTag(doc.skillRun);
    const tagCount = [run, status, warning].filter(Boolean).length;
    const hasTags = tagCount > 0;
    const wide = TAGS_WIDE[tagCount >= 3 ? "many" : "some"];
    const tags = (
        <>
            {run && (
                <RowTag tone={run.tone}>
                    {run.going && <Loader2Icon aria-hidden size={12} className="mr-1 inline-block animate-spin align-[-2px]" />}
                    {run.label}
                </RowTag>
            )}
            {status && <RowTag tone={status.tone}>{status.label}</RowTag>}
            {warning === "red" && <RowTag tone="red">{redTag}</RowTag>}
            {warning === "amber" && amber &&
                (amber.code === "resolved_without_agreement" ? (
                    // The same dot and words as the drawer's flag, not a pill.
                    // One line from 640px; below that it wraps, or at 390px it
                    // pushed the staff Client page sideways (sweep, 14 Oct 2026).
                    <span className={cn(TYPE.tc, "inline-flex items-center gap-1.5 font-medium text-amber-800 sm:whitespace-nowrap")}>
                        <Dot tone="amber" />
                        Resolved without the client&rsquo;s agreement
                    </span>
                ) : (
                    <RowTag tone="amber">
                        {amber.code === "word_file" && doc.convertedTo ? "Converted to a Google Doc" : DOC_FLAG[amber.code]?.label ?? UNKNOWN_FLAG_LABEL}
                    </RowTag>
                ))}
            {clientLate && <RowTag tone="rose">Client late</RowTag>}
        </>
    );
    const chipList = [
        // a doc type staff named themselves (ourteam-backend#6516), told apart from the five
        ...(doc.custom ? [<Tag key="custom" tone="gray">Custom</Tag>] : []),
        ...chips.map((c) => (
            <FamilyChip key={c.label} family={c.family}>
                {c.label}
            </FamilyChip>
        )),
    ];
    // What the client last said, only in the Needs us group, where it is ours
    // to answer. By code lists a doc once per code: there it is in the drawer.
    const last = group === "needs_us" && !showStatus && doc.lastClientPost?.text.trim() ? doc.lastClientPost : null;

    return (
        <div className={SHELL} data-doc-row={doc.id}>
            <div className={ROW}>
                <IconSquare tone={file.tone} icon={file.icon} />
                <div className="min-w-0 flex-1">
                    <div className="flex min-h-8 min-w-0 items-center gap-2">
                        <FullName name={doc.name}>
                            <button
                                type="button"
                                data-doc-open={doc.id}
                                onClick={() => onOpen(doc.id)}
                                className={cn(
                                    TYPE.tb,
                                    "block min-w-0 text-left group-hover:text-blue-600",
                                    muted ? "text-gray-500" : "text-gray-900",
                                    // The title's button covers the whole row, so the
                                    // row opens from anywhere; its focus ring draws round
                                    // the row too.
                                    "after:absolute after:inset-0 after:rounded-lg after:content-['']",
                                    "focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-blue-600",
                                )}
                            >
                                <span className={TITLE_CUT}>{title}</span>
                                {/* The group head says the status; a screen reader hears it with the row. */}
                                <span className="sr-only">, {style.label}</span>
                            </button>
                        </FullName>
                        {chipList.length > 0 && <span className="hidden shrink-0 gap-1 [@container(min-width:40rem)]:flex">{chipList}</span>}
                    </div>
                    {/* The line under the title: narrow, the chips, the fact and the
                        tags; from 40rem the tags alone; once the tags fit on line 1,
                        nothing. Beside the title they would squeeze it to a word. */}
                    {(chipList.length > 0 || fact || hasTags) && (
                        <div
                            className={cn(
                                "flex flex-wrap items-center gap-x-2 gap-y-1",
                                hasTags ? wide.hide : NARROW_ONLY,
                            )}
                        >
                            {chipList.length > 0 && <span className={cn("flex gap-1", NARROW_ONLY)}>{chipList}</span>}
                            {fact && <Fact fact={fact} className={NARROW_ONLY} />}
                            {tags}
                        </div>
                    )}
                    {meta && <p className={cn(TYPE.tc, "m-0 truncate text-gray-500")}>{meta}</p>}
                    {last && (
                        <p className={cn(TYPE.tc, "m-0 mt-0.5 truncate text-gray-600")}>
                            {last.author.name}: &ldquo;{trimText(last.text, 240)}&rdquo;
                        </p>
                    )}
                </div>
                {(hasTags || fact) && (
                    <div className={cn("hidden h-8 shrink-0 items-center gap-2", fact ? "[@container(min-width:40rem)]:flex" : wide.show)}>
                        {hasTags && <span className={cn("hidden items-center gap-2", wide.show)}>{tags}</span>}
                        {fact && <Fact fact={fact} />}
                    </div>
                )}
                {/* A file with no code keeps a labelled "Pick a code": narrow, it
                    drops under the text, lined up with it, so the name keeps the width. */}
                {plain && (
                    <span className="relative z-10 order-last flex h-8 w-full shrink-0 items-center pl-11 [@container(min-width:40rem)]:order-none [@container(min-width:40rem)]:w-auto [@container(min-width:40rem)]:pl-0">
                        <PillButton
                            size="sm"
                            icon={HashIcon}
                            writes
                            data-doc-pick={doc.id}
                            onClick={() => onPick(doc.id)}
                            aria-label={`${doc.notClientDoc ? "Change the code" : "Pick a code"} for ${doc.name}`}
                            className={TAP_TARGET}
                        >
                            {doc.notClientDoc ? "Change" : "Pick a code"}
                        </PillButton>
                    </span>
                )}
                {/* A Word file: convert it from here, or, once converted, open the Google Doc it became. Above the row's own button, like "Pick a code". */}
                {converted ? (
                    <span className="relative z-10 order-last flex h-8 w-full min-w-0 shrink-0 items-center pl-11 [@container(min-width:40rem)]:order-none [@container(min-width:40rem)]:w-auto [@container(min-width:40rem)]:max-w-[16rem] [@container(min-width:40rem)]:pl-0">
                        <button
                            type="button"
                            data-doc-converted={doc.id}
                            onClick={() => onOpen(converted.id)}
                            className={cn(TYPE.tc, TAP_TARGET, "min-w-0 truncate text-left font-medium text-blue-700 hover:underline")}
                        >
                            Converted to a Google Doc: {converted.name}
                        </button>
                    </span>
                ) : doc.fileType === "word" && onCreate ? (
                    <span className="relative z-10 order-last flex h-8 w-full shrink-0 items-center pl-11 [@container(min-width:40rem)]:order-none [@container(min-width:40rem)]:w-auto [@container(min-width:40rem)]:pl-0">
                        <PillButton size="sm" writes data-doc-convert={doc.id} onClick={() => onCreate(prefillFromWord(doc))} aria-label={`Convert to a Google Doc: ${doc.name}`} className={TAP_TARGET}>
                            Convert to a Google Doc
                        </PillButton>
                    </span>
                ) : null}
                {/* Done without the client (#4541): staff finish a doc here, or take it back. Only where
                    the backend serves `done`, and never on a file with no code or an internal one. */}
                {/* Mark as done only on a Google Doc: a Word file's next step is its conversion (#4561). Undo on any Done row. */}
                {canMarkDone && doc.done !== undefined && !plain && (group === "done" || doc.fileType === "gdoc" || doc.fileType === "pdf") && (
                    <span className="relative z-10 order-last flex min-h-8 w-full shrink-0 flex-wrap items-center gap-2 pl-11 [@container(min-width:40rem)]:order-none [@container(min-width:40rem)]:w-auto [@container(min-width:40rem)]:pl-0">
                        {/* Mark as done opens MarkDoneDialog, which asks the day; Undo is one click. */}
                        <PillButton
                            size="sm"
                            variant={group === "done" ? "text" : "secondary"}
                            writes
                            icon={group === "done" ? undefined : CheckIcon}
                            busy={doneBusy}
                            data-doc-done={doc.id}
                            onClick={() => (group === "done" ? void undoDone() : openDone())}
                            aria-label={group === "done" ? `Undo done: ${title}` : `Mark as done: ${title}`}
                            className={TAP_TARGET}
                        >
                            {group === "done" ? "Undo" : "Mark as done"}
                        </PillButton>
                        {doneFailed && <span role="alert" className={cn(TYPE.tc, "text-rose-600")}>{doneFailed}</span>}
                    </span>
                )}
                {/* Delete (#4631): above the row's own button, always shown (a phone has no hover).
                    The dialog asks for the file name typed before anything is sent. */}
                {canDelete && (
                    <button
                        type="button"
                        data-doc-delete={doc.id}
                        disabled={writesPaused}
                        onClick={() => setDeleting(true)}
                        aria-label={`Delete ${doc.name}`}
                        title="Delete"
                        className={cn(
                            "relative z-10 mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full text-gray-400 transition-colors hover:bg-rose-50 hover:text-rose-600 motion-reduce:transition-none",
                            FOCUS_RING,
                            TAP_TARGET,
                        )}
                    >
                        <Trash2Icon aria-hidden size={14} />
                    </button>
                )}
                {canDelete && <DeleteDocDialog doc={doc} open={deleting} onClose={() => setDeleting(false)} />}
                {canMarkDone && group !== "done" && <MarkDoneDialog doc={doc} title={title} open={doneOpen} onClose={() => setDoneOpen(false)} />}
                {/* The real file, on hover or keyboard focus (the kit's RowOpenLink, moved from here). */}
                <RowOpenLink href={doc.url} label={`${file.openLabel}: ${title}`} tip={file.openLabel} />
                {viewLabel ? (
                    <span aria-hidden className={cn(TYPE.tc, "flex h-8 shrink-0 items-center gap-1 self-center font-medium text-blue-600 group-hover:text-blue-700 [@container(min-width:40rem)]:self-auto")}>
                        {viewLabel}
                        <ChevronRightIcon size={12} />
                    </span>
                ) : (
                    <span aria-hidden className="flex h-8 shrink-0 items-center self-center text-gray-400 [@container(min-width:40rem)]:self-auto">
                        <ChevronRightIcon size={14} />
                    </span>
                )}
            </div>
        </div>
    );
}

/**
 * A doc the plan expects by now with none shared: named as the doc to send,
 * with the codes it clears ("Answer Plan", S1 S3 B1) and when it is due, "Due
 * by Fri 16 Oct", or "Was due Mon 14 Sep" in rose once past. The plan lines
 * it clears and the plan window are its tooltips; By code says both in full.
 * A code no catalogue doc covers is its own row, named by its plan line. Not
 * a control: there is no doc to open yet. With the Docs write switch on, the
 * board passes "Create" as its `action`, which drops under the text when narrow.
 */
export function NotSentRow({ doc, action }: { doc: DocToSend; action?: ReactNode }) {
    const first = doc.rows[0];
    const row = { expectedBy: doc.expectedBy, expectedMonth: doc.expectedMonth, state: "not_sent_yet" as const, late: doc.late };
    const due = codeDueText(row);
    const lines = doc.rows.map((r) => (r.label ? codeRowName(r.label, r.outlet) : null)).filter((l): l is string => !!l);
    const title = doc.docType ?? (first.label ? codeRowName(first.label, first.outlet) : codeLabel(first.code, first.outlet));
    const planTip = doc.docType && lines.length > 0
        ? `${doc.rows.length === 1 ? "Plan line: " : `${doc.rows.length} plan lines: `}${lines.join("; ")}`
        : title;
    // S2 wants 1 pack per outlet; G1 1 per plan month started.
    const counts = doc.rows.filter((r) => r.expectedDocs !== null && r.expectedDocs > 1).map((r) => `${r.sharedDocs} of ${r.expectedDocs} shared`);
    const chips = doc.rows.map((r) => (
        <FamilyChip key={codeLabel(r.code, r.outlet)} family={codeFamily(r.code)}>
            {codeLabel(r.code, r.outlet)}
        </FamilyChip>
    ));
    const fact = (counts.length > 0 || due) && (
        <span className={cn(TYPE.tc, "tabular-nums text-gray-500")}>
            {counts.length > 0 && `${counts.join(" · ")}${due ? " · " : ""}`}
            {due && (
                <span title={codeDueText(row, true) ?? undefined} className={cn(doc.late && LATE_TEXT)}>
                    {due}
                </span>
            )}
        </span>
    );
    return (
        <div className={SHELL} data-not-sent={doc.key}>
            <div className={ROW}>
                <IconSquare tone="gray" icon={FileQuestionIcon} />
                <div className="min-w-0 flex-1">
                    <div className="flex min-h-8 min-w-0 items-center gap-2">
                        <p title={planTip} className={cn(TYPE.tb, "m-0 min-w-0 text-gray-900", TITLE_CUT)}>
                            {title}
                        </p>
                        <span className="hidden shrink-0 gap-1 [@container(min-width:40rem)]:flex">{chips}</span>
                    </div>
                    <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-1", NARROW_ONLY)}>
                        <span className="flex gap-1">{chips}</span>
                        {fact}
                    </div>
                </div>
                {fact && <div className="hidden h-8 shrink-0 items-center whitespace-nowrap [@container(min-width:40rem)]:flex">{fact}</div>}
                {action ? (
                    <span className="relative z-10 order-last flex h-8 w-full shrink-0 items-center pl-11 [@container(min-width:40rem)]:order-none [@container(min-width:40rem)]:w-auto [@container(min-width:40rem)]:pl-0">
                        {action}
                    </span>
                ) : (
                    // Where a doc row has its Open icon and chevron, so the dates line up.
                    <span aria-hidden className="hidden shrink-0 gap-3 [@container(min-width:40rem)]:flex">
                        <span className="w-7" />
                        <span className="w-3.5" />
                    </span>
                )}
            </div>
        </div>
    );
}

/** Placeholder rows while the first read is on its way: never a 0. */
export function LoadingRows({ count = 3 }: { count?: number }) {
    return (
        <div aria-hidden>
            {Array.from({ length: count }, (_, i) => (
                <div key={i} className="flex min-h-12 items-center gap-3 border-t border-gray-100 first:border-t-0">
                    <span className="h-8 w-8 rounded-[10px] bg-gray-100" />
                    <span className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                </div>
            ))}
        </div>
    );
}
